import { type ApolloCache, type Reference, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";

import { DELETE_CUPA, GET_CUPA } from "./schema";

/**
 * Removes a deleted CUPA from the cache (list + evict entity).
 * Use from mutation update like: update(cache, { data }) { if (data?.deleteCupa) removeCupaFromCache(cache, data.deleteCupa); }
 */
function removeCupaFromCache(cache: ApolloCache<unknown>, deleted: { id: string }) {
  const ref = cache.identify({ __typename: "Cupa", id: deleted.id }) ?? cache.identify(deleted);
  if (!ref) return;
  cache.modify({
    fields: {
      cupas(existing) {
        if (existing == null || typeof existing !== "object" || "__ref" in existing) {
          return existing;
        }
        const list = existing as { items: Reference[]; count?: number };
        if (!list.items) return existing;
        const newItems = list.items.filter((item: Reference) => item.__ref !== ref);
        if (newItems.length === list.items.length) return existing;
        return {
          ...list,
          items: newItems,
          count: Math.max(0, (list.count ?? 0) - 1),
        };
      },
    },
  });
  cache.evict({ id: ref });
  cache.gc();
}

type ConfirmDeleteCupaProps = {
  cupaId: string;
  onClose: () => void;
};

export function ConfirmDeleteCupa({ cupaId, onClose }: ConfirmDeleteCupaProps) {
  const alerts = useAlerts();

  const { data, previousData } = useQuery(GET_CUPA, {
    fetchPolicy: "cache-and-network",
    variables: { id: cupaId },
  });

  const [deleteCupa, { loading: isDeleting }] = useMutation(DELETE_CUPA);

  const cupa = data?.cupa ?? previousData?.cupa;

  if (!cupa) {
    return null;
  }

  return (
    <ConfirmDialog
      open
      header="Delete CUPA?"
      resources={[
        {
          resourceType: "CUPA",
          resourceName: `${cupa.cupaCode} (${cupa.displayName})`,
        },
      ]}
      loading={isDeleting}
      onClose={onClose}
      onConfirm={() =>
        deleteCupa({
          variables: { id: cupaId },
          onCompleted: () => {
            onClose();
            alerts.success("CUPA deleted successfully");
          },
          onError: (e) => {
            alerts.error("Failed to delete CUPA", e);
          },
          update(cache, { data }) {
            if (!data?.deleteCupa) return;
            removeCupaFromCache(cache, data.deleteCupa);
          },
        })
      }
    />
  );
}
